import { useInfiniteQuery, useMutation, useQuery } from 'react-query'
import { message } from 'antd'

import {
  addFinanceTag,
  archiveFinanceTag,
  deleteFinanceTag,
  editFinanceTag,
  executeImportFinanceTag,
  getFinanceTag,
  getFinanceTagDetail,
  getFinanceTagRoles,
  getFinanceTags,
  getSuggestionFinanceTag,
  unarchiveFinanceTag,
  uploadImportFinanceTag,
} from 'utils/apis'

export function useTags({ params = {}, options = {} }) {
  return useQuery(
    ['tags', params],
    async () => {
      const {
        data: { data },
      } = await getFinanceTags(params)

      return data
    },
    {
      onError: (error) => message.error(error?.data?.message || 'Failed to load tags from server!'),
      ...options,
    },
  )
}

export function useTag({ id, params = {}, enabled = false, options = {} }) {
  return useQuery(
    ['tag', id, params],
    async () => {
      let response
      try {
        const {
          data: { data },
        } = await getFinanceTag(id, params)
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      enabled: !!id && enabled,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export function useTagRoles({ id, params = {}, enabled = false, options = {} }) {
  return useQuery(
    ['tag-roles', id, params],
    async () => {
      let response
      try {
        response = (await getFinanceTagRoles(id, params)).data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      enabled: !!id && enabled,
      onError: (error) =>
        message.error((error && error.message) || 'Failed to load data from server!'),
      ...options,
    },
  )
}

export function useTagDetail({ params, options, extraQueryKey, apiOptions = {} }) {
  return useQuery(
    ['tags', params, extraQueryKey],
    async () => {
      let response
      try {
        response = await getFinanceTagDetail(params, apiOptions)
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response.data.data
    },
    options,
  )
}

export const useEditTagMutation = () => {
  return useMutation(editFinanceTag)
}

export const useAddTagMutation = () => {
  return useMutation(addFinanceTag)
}

export const useDeleteTagMutation = () => {
  return useMutation(deleteFinanceTag)
}

export const useArchiveTagMutation = () => {
  return useMutation(archiveFinanceTag)
}

export const useUnarchiveTagMutation = () => {
  return useMutation(unarchiveFinanceTag)
}

export const useUploadImportTagMutation = () => {
  return useMutation(uploadImportFinanceTag)
}

export const useExecuteImportTagMutation = () => {
  return useMutation(executeImportFinanceTag)
}

export const useInfiniteTagSuggestion = (payload, options = {}) => {
  return useInfiniteQuery(
    ['infinite/tags/suggestion', payload],
    async ({ pageParam = 0 }) => {
      let response
      try {
        const {
          data: { data },
        } = await getSuggestionFinanceTag({ page: pageParam, ...payload })
        response = data
      } catch (error) {
        throw new Error('Failed to load data from server!')
      }
      return response
    },
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage) {
          return undefined
        }
        return lastPage.current_page < lastPage.last_page ? lastPage.current_page + 1 : undefined
      },
      onError: (error) => message.error(error.message || 'Failed to load data from server!'),
      keepPreviousData: true,
      ...options,
    },
  )
}
